import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { NAV_GROUPS } from "../constants/navigation";
import { EuStars } from "./EuStars";
import { LogOut, ShieldCheck } from "lucide-react";

export default function Sidebar() {
    const { user, logout } = useAuth();
    const location = useLocation();

    return (
        <aside
            data-testid="sidebar"
            className="hidden lg:flex flex-col w-72 shrink-0 bg-[#0D1B3E] text-[#F5F0E8] min-h-screen sticky top-0"
        >
            <div className="px-7 pt-8 pb-6 border-b border-[#F5F0E8]/10">
                <NavLink to="/" data-testid="sidebar-brand" className="flex items-center gap-3">
                    <EuStars size={36} />
                    <div className="leading-none">
                        <div className="font-display text-xl tracking-tight">EuroLaw Hub</div>
                        <div className="label-eyebrow mt-1" style={{ color: "#C9A84C" }}>
                            EU Legal Research Workspace
                        </div>
                    </div>
                </NavLink>
            </div>

            <nav className="flex-1 overflow-y-auto px-3 py-6">
                {NAV_GROUPS.map((group) => (
                    <div key={group.label} className="mb-7">
                        <div className="px-4 pb-2 text-[10px] font-semibold uppercase tracking-[0.24em] text-[#C9A84C]">
                            {group.label}
                        </div>
                        <ul className="space-y-1">
                            {group.items.map((item) => {
                                const Icon = item.icon;
                                const active =
                                    location.pathname === item.to ||
                                    (item.to !== "/app" && location.pathname.startsWith(item.to));
                                return (
                                    <li key={item.key}>
                                        <NavLink
                                            to={item.to}
                                            data-testid={`sidebar-nav-link-${item.key}`}
                                            className={`group flex items-center gap-3 px-4 py-2.5 text-[0.95rem] border-l-2 transition-colors ${
                                                active
                                                    ? "border-[#C9A84C] text-[#C9A84C] bg-[#102347]"
                                                    : "border-transparent text-[#F5F0E8]/80 hover:text-[#F5F0E8] hover:bg-[#102347]"
                                            }`}
                                        >
                                            <Icon size={17} strokeWidth={1.6} />
                                            <span className="font-serif">{item.label}</span>
                                            {item.ready ? null : (
                                                <span className="ml-auto text-[9px] uppercase tracking-[0.18em] text-[#F5F0E8]/40">
                                                    soon
                                                </span>
                                            )}
                                        </NavLink>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                ))}

                {user?.role === "admin" && (
                    <div className="mb-7">
                        <div className="px-4 pb-2 text-[10px] font-semibold uppercase tracking-[0.24em] text-[#C9A84C]">
                            Administration
                        </div>
                        <ul className="space-y-1">
                            <li>
                                <NavLink
                                    to="/app/admin"
                                    data-testid="sidebar-nav-link-admin"
                                    className={`group flex items-center gap-3 px-4 py-2.5 text-[0.95rem] border-l-2 transition-colors ${
                                        location.pathname.startsWith("/app/admin")
                                            ? "border-[#C9A84C] text-[#C9A84C] bg-[#102347]"
                                            : "border-transparent text-[#F5F0E8]/80 hover:text-[#F5F0E8] hover:bg-[#102347]"
                                    }`}
                                >
                                    <ShieldCheck size={17} strokeWidth={1.6} />
                                    <span className="font-serif">Admin dashboard</span>
                                </NavLink>
                            </li>
                        </ul>
                    </div>
                )}
            </nav>

            <div className="px-5 py-5 border-t border-[#F5F0E8]/10">
                <div className="flex items-center gap-3 mb-3">
                    <div className="h-9 w-9 bg-[#C9A84C] text-[#0D1B3E] grid place-items-center font-display text-base">
                        {(user?.name || "?").slice(0, 1).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                        <div className="text-sm font-medium truncate" data-testid="sidebar-user-name">
                            {user?.name}
                        </div>
                        <div className="text-[11px] uppercase tracking-[0.18em] text-[#C9A84C]">
                            {user?.plan_key} plan
                        </div>
                    </div>
                </div>
                <button
                    type="button"
                    data-testid="sidebar-logout-button"
                    onClick={logout}
                    className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#F5F0E8]/70 hover:text-[#C9A84C]"
                >
                    <LogOut size={14} /> Sign out
                </button>
            </div>
        </aside>
    );
}
